// ./src/components/generic/MediaSelector/tabs/LibraryTab.tsx
//
// "Library" — pick from files already uploaded to the account. Filters to the
// requested kind. NOTE: the kind filter is client-side (from mimeType), because
// the server-side `kind` facet isn't in the composed schema until ms_storage is
// redeployed. A page therefore holds mixed kinds; we filter then rely on
// loadMore. Swap to the server filter once deployed (see UploadedFileFilter.kind).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Stack, TextInput, SimpleGrid, Center, Loader, Text, Box, Button } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { UploadedFile } from "@/utils/api/UploadedFileService";
import { useUploadedFiles } from "@/hooks/useUploadedFiles";

import { AppIcon } from "../../AppIcon";
import { SelectableFileCard } from "../../SelectableFileCard";
import { MediaKind, mimeToMediaKind } from "../mediaSelectorConfig";

const PAGE_SIZE = 24;

export interface LibraryTabProps {
  /** Media kind to show. */
  kind: MediaKind;
  /** Whether the parent drawer is open (drives the initial load). */
  opened: boolean;
  /** Shared search text (persists across tabs). */
  query: string;
  onQueryChange: (q: string) => void;
  /** Currently selected file id (controlled highlight, single-select). */
  selectedFileId: string | null;
  /** Multi-select: highlight every id in this set + toggle on click. */
  multiSelect?: boolean;
  selectedFileIds?: string[];
  /** A file to surface + preselect on open even if not on the first page. */
  preselectId?: string | null;
  /** Freshly uploaded files to prepend (shown on top of the catalog). */
  extraFiles?: UploadedFile[];
  /** Called when the selection changes (null when deselected). */
  onPick: (file: UploadedFile | null) => void;
  /** Called once the preselected file is fetched, so it can be primed as the pick. */
  onPreselectResolved?: (file: UploadedFile) => void;
}

export const LibraryTab: React.FC<LibraryTabProps> = ({
  kind,
  opened,
  query,
  onQueryChange,
  selectedFileId,
  multiSelect = false,
  selectedFileIds = [],
  preselectId,
  extraFiles = [],
  onPick,
  onPreselectResolved,
}) => {
  const { t } = useTranslation(["common"]);

  const [debouncedSearch] = useDebouncedValue(query, 300);
  const [pinnedFile, setPinnedFile] = useState<UploadedFile | null>(null);

  const { uploadedFiles, isLoaded, loadingMore, hasMore, list, loadMore, getMany } = useUploadedFiles({
    pageSize: PAGE_SIZE,
    autoLoad: false,
  });

  // Initial + search-driven load while open.
  useEffect(() => {
    if (!opened) {
      return;
    }

    const filter = debouncedSearch.trim() ? { nameLike: debouncedSearch.trim() } : {};
    list(filter, { page: 1, pageSize: PAGE_SIZE }, [{ key: "createdAt", direction: "desc" }], true);
  }, [opened, debouncedSearch, list]);

  // Surface the currently-set file even if it's not on the first page, so the
  // preselect highlight is visible on open.
  const preselectFetchedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!opened || !preselectId || preselectFetchedFor.current === preselectId) {
      return;
    }

    preselectFetchedFor.current = preselectId;
    getMany([preselectId]).then((files) => {
      if (files && files[0]) {
        setPinnedFile(files[0]);
        onPreselectResolved?.(files[0]);
      }
    });
  }, [opened, preselectId, getMany, onPreselectResolved]);

  useEffect(() => {
    if (!opened) {
      setPinnedFile(null);
      preselectFetchedFor.current = null;
    }
  }, [opened]);

  // Merge pinned + freshly-uploaded + catalog, de-duped, filtered to the kind.
  const files = useMemo(() => {
    const merged: UploadedFile[] = [];
    const seen = new Set<string>();

    const push = (list: UploadedFile[]) => {
      list.forEach((f) => {
        if (f.id && !seen.has(f.id) && mimeToMediaKind(f.mimeType) === kind) {
          seen.add(f.id);
          merged.push(f);
        }
      });
    };

    push(extraFiles);
    if (pinnedFile) {
      push([pinnedFile]);
    }
    push(uploadedFiles);

    return merged;
  }, [extraFiles, pinnedFile, uploadedFiles, kind]);

  const handleSelect = useCallback(
    (fileId: string, selected: boolean) => {
      // Multi-select: every click emits the file so the parent toggles it.
      if (multiSelect) {
        const file = files.find((f) => f.id === fileId) || null;
        if (file) onPick(file);
        return;
      }

      if (!selected) {
        onPick(null);
        return;
      }

      const file = files.find((f) => f.id === fileId) || null;
      onPick(file);
    },
    [files, onPick, multiSelect]
  );

  // ⚠ The FIRST read only — `loading && files.length === 0` also fired on every reload that came
  // back empty, flashing the tile grid back to a spinner. ▶ npm run check-list-states
  const firstReadPending = !isLoaded && !pinnedFile;

  return (
    <Stack gap="sm" h="100%">
      <TextInput
        size="md"
        placeholder={t("common:mediaSelector.searchLibrary", { defaultValue: "Search your files…" })}
        value={query}
        onChange={(e) => onQueryChange(e.currentTarget.value)}
        leftSection={<AppIcon iconName="Search" size={16} />}
      />

      {firstReadPending && (
        <Center py="xl">
          <Loader size="md" />
        </Center>
      )}

      {!firstReadPending && files.length === 0 && (
        <Center py="xl">
          <Box ta="center">
            <AppIcon iconName="FolderOpen" size={44} style={{ color: "var(--mantine-color-gray-4)" }} />
            <Text size="sm" c="dimmed" mt="sm">
              {debouncedSearch.trim()
                ? t("common:mediaSelector.noMatch", { defaultValue: "No files match your search." })
                : t("common:mediaSelector.emptyLibrary", {
                    defaultValue: "No files here yet. Use the Upload tab to add one.",
                  })}
            </Text>
          </Box>
        </Center>
      )}

      {files.length > 0 && (
        <>
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
            {files.map((file) => (
              <SelectableFileCard
                key={file.id}
                file={file}
                selected={multiSelect ? selectedFileIds.includes(file.id ?? "") : file.id === selectedFileId}
                onSelect={handleSelect}
                // ⚠ The card's fixed 180px is wider than a phone's two columns (~165px): the tiles ran into each other.
                fullWidth
              />
            ))}
          </SimpleGrid>

          {hasMore && (
            <Center>
              <Button variant="subtle" size="md" loading={loadingMore} onClick={() => loadMore()}>
                {t("common:mediaSelector.loadMore", { defaultValue: "Load more" })}
              </Button>
            </Center>
          )}
        </>
      )}
    </Stack>
  );
};
